import React, { useMemo, useRef, useState, useCallback } from 'react';

import { useGlobalAskAica } from '../../../../../../../store/globalAskAica/useGlobalAskAica';
import { ASK_BACK_PROMPT_PREFIX } from '../../../../../../../store/globalAskAica/constants';
import { GlobalMessage } from '../../../../../../../store/globalAskAica/types';

import { AskAicaSurfaceProvider, AskAicaSurfaceContextValue, ASK_AICA_SURFACE } from './AskAicaSurfaceContext';

/**
 * Thin passthrough of useGlobalAskAica() onto the shared surface context — the
 * org-less Portfolio surface (RECUR-27369). Fully isolated from the leads slice:
 * this only maps useGlobalAskAica()'s own return value.
 */
export const PortfolioAskAicaSurfaceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const {
        messages,
        isLoading,
        isLoadingChatHistory,
        error,
        currentCompany,
        companyLess,
        askBack,
        decline,
        conversationId,
        conversations,
        isLoadingConversations,
        selectedSources,
        sendMessage,
        replyToGate,
        dismissAskBack,
        clearCompany,
        pinCompany,
        newChat,
        clearChat,
        deleteConversation,
        pendingNewConversation,
        loadConversation,
        reasoning,
        openChat,
        fetchUserConversations,
        isClearingChat,
        hasMoreData,
        registerStreamStartedCallback,
    } = useGlobalAskAica();

    const messagesEndRef = useRef<HTMLDivElement>(null);
    // A cross-chat citation's navigation target (RECUR-27466 follow-up) — see
    // the matching comment in LeadsAskAicaSurfaceProvider.
    const [focusMessageId, setFocusMessageId] = useState<string | null>(null);
    const clearFocusMessageId = useCallback(() => setFocusMessageId(null), []);

    // An ask_back turn's raw prompt ("I found multiple companies matching…" OR
    // the confirm-single "Did you mean X? Please confirm.") is never persisted
    // as content for a LIVE turn (see useGlobalAskAica.runTurn) — the docked
    // CompanyPicker IS the whole response. A conversation reloaded from backend
    // history now carries a structural `is_gate_control` flag per message
    // (Athena H-11) — preferred over the English-copy prefix match, which never
    // covered the confirm-single phrasing. The prefix stays as a fallback for
    // messages that don't carry the flag (older history rows the BE hasn't
    // backfilled). Decline turns are NOT flagged — Q7 keeps decline text as a
    // plain bubble (the shared MessagesContainer has no such filter — it must
    // stay leads-generic, see RECUR-27369 plan §3).
    //
    // useGlobalAskAica().messages is chronological ASCENDING (oldest first) —
    // globalAskAica/reducer.ts's addMessage appends, and runTurn relies on that
    // order for its messages.slice(-HISTORY_TURNS) history window. But every
    // shared AskAicaV2 leaf (MessagesContainer, LeftPanel) hard-assumes
    // messages[0] is the NEWEST message — that's the leads askAica/reducer.ts
    // contract (addMessage prepends) the shared surface context was built
    // against. Reverse here, at the adapter boundary, so both surfaces hand the
    // shared leaves the same newest-first contract without touching the
    // chronological order runTurn depends on (RECUR-27369 follow-up).
    const filteredMessages = useMemo(
        () =>
            messages
                .filter(
                    (message: GlobalMessage) =>
                        !(message.type === 'assistant' && (message.is_gate_control || message.content.startsWith(ASK_BACK_PROMPT_PREFIX))),
                )
                .reverse(),
        [messages],
    );

    const value: AskAicaSurfaceContextValue = useMemo(
        () => ({
            surface: ASK_AICA_SURFACE.PORTFOLIO,
            messages: filteredMessages,
            reasoning,
            isLoading,
            isLoadingChatHistory,
            error,
            hasMoreData,
            selectedSources,
            sendMessage: (content: string) => sendMessage(content),
            clearChat,
            fetchChatHistory: () => {},
            // Poll-safe (just refreshes the list) — same as openChat itself, so
            // periodic polling can't override an in-progress manual conversation switch either.
            fetchOrgConversations: fetchUserConversations,
            fetchLibraryFiles: undefined,
            registerStreamStartedCallback,
            messagesEndRef,
            fileContextOrgId: currentCompany?.org_id,
            conversations,
            isLoadingConversations,
            selectedConversationId: conversationId,
            isClearingChat,
            isViewOnly: false,
            activeOrgId: undefined,
            enableFeedbackMention: false,
            currentCompany,
            companyLess,
            askBack,
            decline,
            replyToGate,
            dismissAskBack,
            clearCompany,
            pinCompany,
            newChat,
            deleteConversation,
            pendingNewConversation,
            loadConversation,
            openChat,
            // Portfolio's own switch-conversation mechanism — a citation click into
            // "Related Conversations" reuses it exactly like a sidebar row click does.
            // Wrapped (not passed as loadConversation directly) so the citation's
            // optional targetMessageId has somewhere to land — loadConversation
            // itself stays single-arg since its other callers (sidebar row clicks)
            // never have a target message either. openChat never calls it — the
            // orb always opens to a fresh chat.
            openConversation: (conversationId: string, targetMessageId?: string | null) => {
                loadConversation(conversationId);
                setFocusMessageId(targetMessageId ?? null);
            },
            focusMessageId,
            clearFocusMessageId,
        }),
        [
            filteredMessages,
            reasoning,
            isLoading,
            isLoadingChatHistory,
            error,
            selectedSources,
            sendMessage,
            clearChat,
            fetchUserConversations,
            currentCompany,
            conversations,
            isLoadingConversations,
            conversationId,
            companyLess,
            askBack,
            decline,
            replyToGate,
            dismissAskBack,
            clearCompany,
            pinCompany,
            newChat,
            deleteConversation,
            pendingNewConversation,
            loadConversation,
            openChat,
            isClearingChat,
            hasMoreData,
            registerStreamStartedCallback,
            focusMessageId,
            clearFocusMessageId,
        ],
    );

    return <AskAicaSurfaceProvider value={value}>{children}</AskAicaSurfaceProvider>;
};

export default PortfolioAskAicaSurfaceProvider;
